import type { ChangeEvent } from 'react';

import { FieldChangeTrigger } from '../../../enums/form';
import type { FieldChangeTriggerHandler } from '../../../types/form';
import type { DispatchState } from '../../../types/React';
import { isValueValid } from '../utils';

interface GetValidatedChangeHandlerOptions {
  regex?: RegExp;
  maxLength?: number;
  hasError?: boolean;
  onChange: (newValue: string) => void;
}

interface GetChangeHandlerOptions<FieldName> {
  name: FieldName;
  replace?: TextFieldReplace;
  setInputValue: DispatchState<string>;
  onChange: FieldChangeTriggerHandler<string>;
}

interface GetTextFieldChangeHandlerOptions<FieldName>
  extends GetChangeHandlerOptions<FieldName>, Omit<GetValidatedChangeHandlerOptions, 'onChange'> {}

export type TextFieldReplace = (value: string) => string;

export const getTrimmedValue = (shouldTrim: boolean, value: string) =>
  shouldTrim ? value.trim() : value || '';

const getReplacedValue = (value: string, replace?: TextFieldReplace) =>
  replace ? replace(value) : value;

const getChangeHandler =
  <FieldName extends string = string>({
    name,
    replace,
    setInputValue,
    onChange,
  }: GetChangeHandlerOptions<FieldName>) =>
  (newValue: string) => {
    const safeValue = getReplacedValue(newValue, replace);

    setInputValue(safeValue);
    onChange({
      fieldName: name,
      value: safeValue,
      trigger: FieldChangeTrigger.Change,
    });
  };

const getValidatedChangeHandler =
  ({ regex, maxLength, hasError, onChange }: GetValidatedChangeHandlerOptions) =>
  ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value: newValue } = target;

    if (hasError || isValueValid({ value: newValue, regex, maxLength })) {
      onChange(newValue);
    }
  };

export const getTextFieldChangeHandler = <FieldName extends string = string>({
  name,
  regex,
  maxLength,
  hasError,
  replace,
  setInputValue,
  onChange,
}: GetTextFieldChangeHandlerOptions<FieldName>) => {
  const handleChange = getChangeHandler({
    name,
    replace,
    setInputValue,
    onChange,
  });

  const validatedHandleChange = getValidatedChangeHandler({
    regex,
    maxLength,
    hasError,
    onChange: handleChange,
  });

  return validatedHandleChange;
};
